import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from '@slorber/react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { 
    Loader2, 
    ArrowLeft, 
    BookOpen,
    GraduationCap,
    Users,
    ChevronRight,
    ExternalLink
} from 'lucide-react';
import api from '../lib/api';

// Helper to generate instructor slug
const nameToSlug = (name) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export const MethodPage = () => {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [instructors, setInstructors] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [methodRes, instructorsRes] = await Promise.all([
                    api.get(`/seo/methods/${slug}`),
                    api.get('/instructors')
                ]);
                setData(methodRes.data);
                setInstructors(instructorsRes.data);
            } catch (err) {
                console.error('Failed to fetch method:', err);
                setError(err.response?.data?.detail || 'Failed to load content');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchData();
        }
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="method-loading">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="section-gap" data-testid="method-error">
                <div className="container-main text-center">
                    <h1 className="text-2xl font-semibold mb-4">Content Not Found</h1>
                    <p className="text-muted-foreground mb-6">{error}</p>
                    <Button asChild variant="outline">
                        <Link to="/">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Home
                        </Link>
                    </Button>
                </div>
            </div>
        );
    }

    // Get related instructors
    const relatedInstructors = instructors.filter(inst => 
        data.related_instructors?.includes(nameToSlug(inst.name))
    );

    return (
        <>
            {/* SEO Meta Tags */}
            <Helmet>
                <title>{data.meta_title}</title>
                <meta name="description" content={data.meta_description} />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={`https://zarolla.com/methods/${slug}`} />
                
                {/* Open Graph */}
                <meta property="og:title" content={data.meta_title} />
                <meta property="og:description" content={data.meta_description} />
                <meta property="og:type" content="article" />
                <meta property="og:image" content={data.hero_image} />
                
                {/* Schema.org Article markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": data.h1,
                        "description": data.meta_description,
                        "image": data.hero_image,
                        "author": {
                            "@type": "Organization",
                            "name": "Zarolla Online Art Academy"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "Zarolla Online Art Academy",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://customer-assets.emergentagent.com/job_video-rebuild-2/artifacts/96keensf_Zarolla_logo_final_allwhite_nosub.png"
                            }
                        }
                    })}
                </script>
            </Helmet>

            <article data-testid="method-page" className="pb-16">
                {/* Hero Section */}
                <div className="relative h-[400px] overflow-hidden">
                    <img 
                        src={data.hero_image} 
                        alt={data.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                        <div className="container-main">
                            <Link 
                                to="/" 
                                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-4"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to Home
                            </Link>
                            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white max-w-4xl">
                                {data.h1}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className="container-main mt-12">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
                        {/* Main Content - 3 columns */}
                        <div className="lg:col-span-3 space-y-12">
                            {/* Introduction */}
                            <section>
                                <div className="prose prose-lg max-w-none">
                                    {data.introduction.split('\n\n').map((para, i) => (
                                        <p key={i} className="text-muted-foreground leading-relaxed">
                                            {para}
                                        </p>
                                    ))}
                                </div>
                            </section>

                            {/* Main Sections */}
                            {data.sections?.map((section, index) => (
                                <section key={index} id={`section-${index}`}>
                                    <h2 className="font-serif text-2xl md:text-3xl mb-6">
                                        {section.heading}
                                    </h2>
                                    <div className="prose prose-lg max-w-none">
                                        {section.content.split('\n\n').map((para, i) => {
                                            // Handle bold text and lists
                                            if (para.startsWith('**') || para.includes('**')) {
                                                return (
                                                    <div key={i} className="text-muted-foreground leading-relaxed mb-4"
                                                         dangerouslySetInnerHTML={{
                                                             __html: para
                                                                 .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-foreground">$1</strong>')
                                                                 .replace(/\n/g, '<br />')
                                                         }}
                                                    />
                                                );
                                            }
                                            if (para.startsWith('-') || para.startsWith('1.')) {
                                                const items = para.split('\n').filter(Boolean);
                                                return (
                                                    <ul key={i} className="list-disc pl-6 space-y-2 mb-4">
                                                        {items.map((item, j) => (
                                                            <li key={j} className="text-muted-foreground">
                                                                {item.replace(/^[-\d.]\s*/, '')}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                );
                                            }
                                            return (
                                                <p key={i} className="text-muted-foreground leading-relaxed mb-4">
                                                    {para}
                                                </p>
                                            );
                                        })}
                                    </div>
                                </section>
                            ))}

                            {/* FAQ Section */}
                            {data.faq && data.faq.length > 0 && (
                                <section id="faq">
                                    <h2 className="font-serif text-2xl md:text-3xl mb-6">
                                        Frequently Asked Questions
                                    </h2>
                                    <Accordion type="single" collapsible className="w-full">
                                        {data.faq.map((item, index) => (
                                            <AccordionItem key={index} value={`faq-${index}`}>
                                                <AccordionTrigger className="text-left">
                                                    {item.question}
                                                </AccordionTrigger>
                                                <AccordionContent className="text-muted-foreground">
                                                    {item.answer}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                    
                                    {/* FAQ Schema */}
                                    <Helmet>
                                        <script type="application/ld+json">
                                            {JSON.stringify({
                                                "@context": "https://schema.org",
                                                "@type": "FAQPage",
                                                "mainEntity": data.faq.map(item => ({
                                                    "@type": "Question",
                                                    "name": item.question,
                                                    "acceptedAnswer": {
                                                        "@type": "Answer",
                                                        "text": item.answer
                                                    }
                                                }))
                                            })}
                                        </script>
                                    </Helmet>
                                </section>
                            )}
                        </div>

                        {/* Sidebar - 1 column */}
                        <aside className="lg:col-span-1 space-y-6">
                            {/* Table of Contents */}
                            <Card className="sticky top-24">
                                <CardContent className="pt-6">
                                    <h3 className="font-medium text-sm uppercase tracking-wider mb-4 text-muted-foreground">
                                        In This Guide
                                    </h3>
                                    <nav className="space-y-2">
                                        {data.sections?.map((section, index) => (
                                            <a 
                                                key={index}
                                                href={`#section-${index}`}
                                                className="block text-sm text-muted-foreground hover:text-accent transition-colors py-1"
                                            >
                                                {section.heading}
                                            </a>
                                        ))}
                                        {data.faq && data.faq.length > 0 && (
                                            <a 
                                                href="#faq"
                                                className="block text-sm text-muted-foreground hover:text-accent transition-colors py-1"
                                            >
                                                FAQ
                                            </a>
                                        )}
                                    </nav>
                                </CardContent>
                            </Card>

                            {/* Related Instructors */}
                            {relatedInstructors.length > 0 && (
                                <Card>
                                    <CardContent className="pt-6">
                                        <h3 className="font-medium text-sm uppercase tracking-wider mb-4 text-muted-foreground flex items-center gap-2">
                                            <Users className="h-4 w-4" />
                                            Related Instructors
                                        </h3>
                                        <div className="space-y-3">
                                            {relatedInstructors.map((inst, index) => (
                                                <Link 
                                                    key={index}
                                                    to={`/instructor/${nameToSlug(inst.name)}`}
                                                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors"
                                                >
                                                    <img 
                                                        src={inst.image} 
                                                        alt={inst.name}
                                                        className="w-10 h-10 rounded-full object-cover"
                                                    />
                                                    <span className="text-sm font-medium">{inst.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Related Methods */}
                            {data.related_methods && data.related_methods.length > 0 && (
                                <Card>
                                    <CardContent className="pt-6">
                                        <h3 className="font-medium text-sm uppercase tracking-wider mb-4 text-muted-foreground flex items-center gap-2">
                                            <BookOpen className="h-4 w-4" />
                                            Related Topics
                                        </h3>
                                        <div className="space-y-2">
                                            {data.related_methods.map((method, index) => (
                                                <Link 
                                                    key={index}
                                                    to={`/methods/${method}`}
                                                    className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary transition-colors text-sm"
                                                >
                                                    <span className="capitalize">{method.replace(/-/g, ' ')}</span>
                                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                </Link>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* CTA */}
                            <Card className="bg-accent/10 border-accent/30">
                                <CardContent className="pt-6">
                                    <GraduationCap className="h-8 w-8 text-accent mb-3" />
                                    <h3 className="font-medium mb-2">Start Learning Today</h3>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Explore our courses and workshops from master artists.
                                    </p>
                                    <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                                        <Link to="/courses">
                                            Browse Courses
                                        </Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        </aside>
                    </div>
                </div>
            </article>
        </>
    );
};

export default MethodPage;
